import { useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { AlertTriangle } from "lucide-react"
import { useQuery } from "@tanstack/react-query"

// ** Theme Components
import { Button } from "@pma/components/common/button"
import { Alert, AlertDescription, AlertTitle } from "@pma/components/common/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@pma/components/common/alert-dialog"

// ** Routes
import { GET_ORGANIZATION, SERVER_URL } from "@pma/utils/api-routes"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Custom Context
import { useWarnings } from "@pma/contexts/WarningsContext"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

const WarningAlert = ({ isOpen }: { isOpen: boolean }) => {
  const { warnings } = useWarnings()
  const { authConfig, signIn } = useAuth()
  const { translate, locale } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  // Fetch organization data
  const orgId = authConfig?.last_organization_id
  const { refetch: refetchOrg } = useQuery({
    queryKey: ["workspace-details", orgId],
    queryFn: async () => {
      const response = await axios.get(SERVER_URL + GET_ORGANIZATION + `/${orgId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      return response?.data
    },
    enabled: !!orgId && !!accessToken,
  })

  // Collect messages into one list
  const messages: string[] = []

  // Limits
  if (warnings.storage_warning.show) {
    messages.push(translate("Storage almost full (90% used)."))
  }
  if (warnings.user_limits_warning.show) {
    messages.push(
      translate("User limit reached:") +
        ` ${warnings.user_limits_warning?.exist}/${warnings.user_limits_warning.limit}`,
    )
  }
  if (warnings.project_limits_warning.show) {
    messages.push(
      translate("Project limit reached:") +
        ` ${warnings.project_limits_warning.exist}/${warnings.project_limits_warning.limit}`,
    )
  }
  if (warnings.task_limits_warning.show) {
    messages.push(
      translate("Task limit reached:") +
        ` ${warnings.task_limits_warning.exist}/${warnings.task_limits_warning.limit}`,
    )
  }

  // Expiration
  if (warnings.subscription_expired.show) {
    messages.push(
      translate("Subscription expires on") + ` ${warnings.subscription_expired.expired}.`,
    )
  }
  if (warnings.trial_subscription_expired.show) {
    messages.push(
      translate("Trial subscription expires on") +
        ` ${warnings.trial_subscription_expired.expired}.`,
    )
  }
  const handleTryPremium = async () => {
    setShowConfirmDialog(false)

    const orgId = authConfig?.last_organization_id
    if (!orgId) {
      return
    }
    try {
      const response = await axios.post(
        SERVER_URL + "/organization/subscription/try",
        { organization_id: orgId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken || ""}`,
          },
        },
      )
      if (response.status === 200 && response.data === "OK") {
        toast({
          title: translate("Success"),
          description: translate("Premium trial activated"),
          variant: "success",
        })

        // Refetch organization data and update the auth context
        const orgData = await refetchOrg()
        if (orgData.data) {
          signIn({ workspace: orgData.data })
        }
      }
    } catch (error) {
      toast({
        title: translate("Error"),
        description: translate("Failed to activate premium trial"),
        variant: "error",
      })
    }
  }

  // Nothing to show
  if (messages.length === 0 && !warnings.trial_offer.show) return null

  return (
    <div
      className={`sticky top-[121px] z-10 my-3 flex items-center justify-between space-x-2 bg-white/90 px-4 py-2 ${
        isOpen
          ? `${locale === "he" ? "lg:mr-52" : "lg:ml-52"}`
          : `${locale === "he" ? "lg:mr-14" : "lg:ml-14"}`
      }`}
    >
      <Alert variant='destructive' className='flex items-center justify-between bg-[#ffffdf]'>
        <div className='mr-7 flex flex-col gap-1'>
          <AlertTitle className='flex items-center gap-2'>
            <AlertTriangle className='h-4 w-4' />
            {translate("Subscription Warnings")}
          </AlertTitle>
          <AlertDescription>
            <ul className='ml-5 list-disc'>
              {messages.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </AlertDescription>
        </div>

        {/* Try Premium button */}
        {warnings.trial_offer.show && (
          <>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => setShowConfirmDialog(true)}
              className='mx-1'
            >
              {translate("Activate trial")}
            </Button>

            <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {translate("Activate Premium Trial")} -{" "}
                    {locale === "he"
                      ? warnings.trial_offer?.subscription_plan?.name
                      : warnings.trial_offer?.subscription_plan?.name_en}
                  </AlertDialogTitle>
                  <AlertDialogDescription className='space-y-4'>
                    <div>
                      <h4 className='mb-2 font-medium'>{translate("Premium Trial Features")}:</h4>
                      <ul className='list-disc space-y-2 pl-6'>
                        <li>{translate("Unlimited projects per team")}</li>
                        <li>{translate("Unlimited members per team")}</li>
                        <li>{translate("Unlimited tasks per project")}</li>
                        <li>{translate("Unlimited storage")}</li>
                      </ul>
                    </div>

                    <div className='mt-4 border-t pt-4'>
                      <p className='text-sm'>
                        {translate("Trial expires on")}:{" "}
                        <span className='font-medium'>{warnings.trial_offer.up_to}</span>
                      </p>
                      <p className='mt-4 text-sm text-muted-foreground'>
                        {translate("Are you sure you want to activate your premium trial?")}
                      </p>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{translate("Cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleTryPremium}>
                    {translate("Continue")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </Alert>
    </div>
  )
}

export default WarningAlert
